import { __awaiter } from "tslib";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useAddTravellersDataMutation, useSaveOrderServicesMutation } from '@websky/graphql';
import { useToggleable } from '../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { setPassengerValues } from '../Checkout/store/passengers/actions';
import { getIsPriceFreezeProductSelected, getIsTravellersDataFilled, getOrder } from '../Checkout/store/order/selectors';
import { getParamsForSaveOrderServicesMutation, isFareLockService, isRefundOptionService } from './utils';
import { fillOrder } from '../Checkout/store/order/actions';
export const useConfirmOption = (order, onOrderLoaded, serviceSelector, setLoading) => {
    var _a, _b, _c;
    const { isOpen: isSelected, toggle, open, close } = useToggleable(false);
    const optionService = useMemo(() => { var _a; return (_a = order === null || order === void 0 ? void 0 : order.additionalServices.gdsServices) === null || _a === void 0 ? void 0 : _a.services.find(serviceSelector); }, [order]);
    const [serviceRequest, { loading }] = useSaveOrderServicesMutation();
    useEffect(() => {
        if (setLoading) {
            setLoading(loading);
        }
    }, [loading]);
    const handleChange = useCallback(() => {
        toggle();
        if (loading) {
            return;
        }
        const travellerServices = getParamsForSaveOrderServicesMutation(order, optionService, isSelected ? 'remove' : 'add');
        serviceRequest({
            variables: {
                params: {
                    id: order.id,
                    services: travellerServices
                }
            }
        }).then(data => {
            var _a;
            if ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.SaveOrderServices) {
                onOrderLoaded(data.data.SaveOrderServices);
            }
        });
    }, [isSelected, order, optionService, serviceRequest, loading]);
    const isAddedToOrder = (_a = order === null || order === void 0 ? void 0 : order.travellers) === null || _a === void 0 ? void 0 : _a.some(traveller => { var _a, _b, _c; return (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => (service === null || service === void 0 ? void 0 : service.serviceId) === (optionService === null || optionService === void 0 ? void 0 : optionService.id)); });
    useEffect(() => {
        if (isAddedToOrder) {
            open();
        }
        else {
            close();
        }
    }, [order]);
    const isConfirmed = (_b = order === null || order === void 0 ? void 0 : order.travellers) === null || _b === void 0 ? void 0 : _b.some(traveller => {
        var _a, _b, _c;
        return (_c = (_b = (_a = traveller === null || traveller === void 0 ? void 0 : traveller.services) === null || _a === void 0 ? void 0 : _a.gdsServices) === null || _b === void 0 ? void 0 : _b.services) === null || _c === void 0 ? void 0 : _c.some(service => (service === null || service === void 0 ? void 0 : service.serviceId) === (optionService === null || optionService === void 0 ? void 0 : optionService.id) && (service === null || service === void 0 ? void 0 : service.confirmedCount));
    });
    return {
        handleChange,
        isSelected,
        isConfirmed,
        toggle,
        loading,
        isAddedToOrder,
        optionService,
        price: {
            amount: ((_c = optionService === null || optionService === void 0 ? void 0 : optionService.price) === null || _c === void 0 ? void 0 : _c.amount) *
                (optionService === null || optionService === void 0 ? void 0 : optionService.allowedPassengers.length) *
                Array.prototype.concat.apply([], optionService === null || optionService === void 0 ? void 0 : optionService.allowedSegments).length,
            currency: optionService === null || optionService === void 0 ? void 0 : optionService.price.currency
        }
    };
};
export const useFareLock = (order, onOrderLoaded, setLoading) => {
    const fareLock = useConfirmOption(order, onOrderLoaded, isFareLockService, setLoading);
    return Object.assign(Object.assign({}, fareLock), { optionService: fareLock.optionService });
};
export const useRefundOption = (order, onOrderLoaded, setLoading) => {
    return useConfirmOption(order, onOrderLoaded, isRefundOptionService, setLoading);
};
export const useUpdateDocumentsFreezeView = () => {
    const [state, setState] = useState('review');
    const toAddDocuments = () => setState('update');
    const toReview = () => setState('review');
    return {
        mode: state,
        toAddDocuments,
        toReview
    };
};
/**
 *
 * get formState and call SetTravellersData mutation with new values
 */
export const useUpdateTravellers = ({ setNextStep }) => {
    const dispatch = useDispatch();
    const order = useSelector(getOrder);
    const isTravellersDataFilled = useSelector(getIsTravellersDataFilled);
    const isPriceFreezeSelected = useSelector(getIsPriceFreezeProductSelected);
    const { values, valid } = useFormState();
    const [setTravellersData, { loading, data }] = useAddTravellersDataMutation();
    useEffect(() => {
        if (!valid) {
            return;
        }
        order.travellers.forEach((traveller, index) => {
            const passengerValues = traveller.values.map(value => {
                return {
                    name: value.name,
                    type: value.type,
                    value: values['passengers'][index][value.name]
                };
            });
            dispatch(setPassengerValues(index, passengerValues));
        });
    }, [values, valid, order.travellers]);
    useEffect(() => {
        const setTravellersData = data === null || data === void 0 ? void 0 : data.SetTravellersData;
        if (isTravellersDataFilled || !(setTravellersData === null || setTravellersData === void 0 ? void 0 : setTravellersData.isTravellersDataFilled)) {
            return;
        }
        setTimeout(() => {
            setNextStep();
            dispatch(fillOrder(setTravellersData));
        });
    }, [data === null || data === void 0 ? void 0 : data.SetTravellersData, isTravellersDataFilled, setNextStep]);
    const handleSubmit = useCallback((passengersValues) => __awaiter(void 0, void 0, void 0, function* () {
        if (!isPriceFreezeSelected && isTravellersDataFilled) {
            return null;
        }
        yield setTravellersData({
            variables: {
                variables: {
                    orderId: order.id,
                    travellersData: passengersValues
                }
            }
        });
    }), [order.id, isPriceFreezeSelected, isTravellersDataFilled]);
    return {
        handleSubmit,
        loading,
        data,
        valid
    };
};
